/// <amd-module name="CodeCoverage/CodeCoverageCiChangedTab/ChangedTab" />
import * as React from "react";

import { ObservableArray, ObservableValue, IObservableValue } from "VSS/Core/Observable";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IFilter, IFilterState } from "VSSUI/Utilities/Filter";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ZeroData } from "VSSUI/ZeroData";

import { CoverageChangeSummaryResponse } from "CodeCoverage/Clients/Coverage/Contracts/Coverage";
import { IChangedTabViewService, ICodeCoverageChangeResponse } from "CodeCoverage/CodeCoverageCiChangedTab/ChangedTabViewService";
import { IChangedTabDataProviderService, ICoverageChangeResponse } from "CodeCoverage/CodeCoverageCiChangedTab/ChangedTabDataProviderService";
import { FilesCoverageTable } from "CodeCoverage/CodeCoverageCiChangedTab/FilesCoverageTable";
import { mapChangedFilesToFileCoverageTableItem } from "CodeCoverage/CodeCoverageCiChangedTab/Helpers";
import * as Resources from "CodeCoverage/CodeCoverageCiChangedTab/Resources";
import { PerfScenarios, TelemetryEvents } from "CodeCoverage/ViewCommon/Constants";
import { IFileCoverageTableItem } from "CodeCoverage/ViewCommon/Contracts/Common";
import { publish } from "CodeCoverage/ViewCommon/Helpers";
import { RunViewArtifact } from "CodeCoverage/ViewCommon/RunViewArtifact";

export interface IChangedTabProps {
    summaryResponse: CoverageChangeSummaryResponse;
    runViewArtifact: RunViewArtifact;
    filter: IFilter;
}

export interface IChangedTabState {
    isLoading: boolean;
}

export class CodeCoverageCiChangedTab extends VssComponent<IChangedTabProps, IChangedTabState> {
    private currentTableItems: ObservableArray<IFileCoverageTableItem>;
    private loadingMore: boolean | undefined;
    private hasMoreChangeData?: boolean;
    private fileCodeCoverageItem: IObservableValue<IFileCoverageTableItem | undefined>;
    private changedTabViewService: IChangedTabViewService;
    private isFilterMode: ObservableValue<boolean>;
    private filterInProgress: ObservableValue<boolean>;
    private filteredItems: ObservableArray<IFileCoverageTableItem>;

    constructor(props: IChangedTabProps, context: IVssComponentContext) {
        super(props, context);

        this.currentTableItems = new ObservableArray<IFileCoverageTableItem>([]);
        this.fileCodeCoverageItem = new ObservableValue(undefined);
        this.isFilterMode = new ObservableValue(false);
        this.filterInProgress = new ObservableValue(false);
        this.filteredItems = new ObservableArray<IFileCoverageTableItem>([]);

        const { summaryResponse, runViewArtifact } = props;
        const hasFileCoverageChanges =
            summaryResponse &&
            summaryResponse.coverageChangeSummary &&
            summaryResponse.coverageChangeSummary.fileCoverageChanges &&
            summaryResponse.coverageChangeSummary.fileCoverageChanges.length > 0;

        this.changedTabViewService = this.context.pageContext.getService<IChangedTabViewService>("IChangedTabViewService");

        if (hasFileCoverageChanges) {
            this.changedTabViewService.initialize(
                runViewArtifact.projectId,
                runViewArtifact.buildId,
                undefined,
                mapChangedFilesToFileCoverageTableItem(summaryResponse.coverageChangeSummary.fileCoverageChanges),
                summaryResponse.continuationToken != null ? summaryResponse.continuationToken : undefined
            );
            const { fileCoverageChanges, hasMoreData } = this.changedTabViewService.getFirstBatchedCoverageChanges();
            this.currentTableItems.value = fileCoverageChanges;
            this.hasMoreChangeData = hasMoreData;
        }

        this.state = {
            isLoading: !hasFileCoverageChanges
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this.props.filter.subscribe(this.onFilterChanged);

        if (this.state.isLoading) {
            this.trackPromise(
                this.context.pageContext
                    .getService<IChangedTabDataProviderService>("IChangedTabDataProviderService")
                    .getCoverageChangesDataAsync(this.props.runViewArtifact.projectId, this.props.runViewArtifact.buildId)
            ).promise.then((response: ICoverageChangeResponse) => {
                this.changedTabViewService.initialize(
                    this.props.runViewArtifact.projectId,
                    this.props.runViewArtifact.buildId,
                    undefined,
                    response.fileCoverageChanges,
                    response.continuationToken
                );
                const { fileCoverageChanges, hasMoreData } = this.changedTabViewService.getFirstBatchedCoverageChanges();
                this.currentTableItems.value = fileCoverageChanges;
                this.hasMoreChangeData = hasMoreData;

                this.setState({ isLoading: false });
            });
        }
    }

    public render(): JSX.Element {
        if (this.state.isLoading) {
            return <Spinner className="cc-ci-changed-spinner flex-grow flex-center" size={SpinnerSize.large} label={Resources.Loading} />;
        } else {
            return (
                <Observer
                    tableItems={this.currentTableItems}
                    fileCodeCoverageItem={this.fileCodeCoverageItem}
                    isFilterMode={this.isFilterMode}
                    filterInProgress={this.filterInProgress}
                    filteredItems={this.filteredItems}
                >
                    {(props: {
                        tableItems: IFileCoverageTableItem[];
                        fileCodeCoverageItem: IFileCoverageTableItem;
                        isFilterMode: boolean;
                        filterInProgress: boolean;
                        filteredItems: IFileCoverageTableItem[];
                    }) => {
                        if (!props.isFilterMode && props.tableItems.length === 0) {
                            return (
                                <div>
                                    <ZeroData
                                        imagePath={getAssetLocation(
                                            this.context.pageContext,
                                            "ms.vss-codecoverage-ci-web/codecoverage-ci-changed-tab/Content/no-changed-files.png"
                                        )}
                                        imageAltText=""
                                        primaryText={Resources.NoChangedFilesPrimaryText}
                                        secondaryText={Resources.NoChangedFilesSecondaryText}
                                    />
                                </div>
                            );
                        } else if (props.isFilterMode && !props.filterInProgress && props.filteredItems.length === 0) {
                            return (
                                <ZeroData
                                    imagePath={getAssetLocation(
                                        this.context.pageContext,
                                        "ms.vss-codecoverage-ci-web/view-common/Content/no-results-image.png"
                                    )}
                                    imageAltText=""
                                    primaryText={Resources.FilteringZeroResultsPrimaryText}
                                    secondaryText={Resources.FilteringZeroResultsSecondaryText}
                                />
                            );
                        }

                        const getTableItems = (
                            items: (IFileCoverageTableItem | ObservableValue<IFileCoverageTableItem | undefined>)[],
                            hasMoreChangesData: boolean
                        ): (IFileCoverageTableItem | ObservableValue<IFileCoverageTableItem | undefined>)[] => {
                            if (hasMoreChangesData) {
                                const tableItems = items.map(x => x);
                                tableItems.push(new ObservableValue(undefined));
                                return tableItems;
                            }

                            return items;
                        };

                        const getFilteredItems = (
                            items: (IFileCoverageTableItem | ObservableValue<IFileCoverageTableItem | undefined>)[],
                            filterInProgress: boolean
                        ): (IFileCoverageTableItem | ObservableValue<IFileCoverageTableItem | undefined>)[] => {
                            return filterInProgress ? [new ObservableValue(undefined)] : items;
                        };

                        return (
                            <>
                                {props.isFilterMode ? (
                                    <FilesCoverageTable
                                        itemsProvider={new ArrayItemProvider(getFilteredItems(props.filteredItems, props.filterInProgress))}
                                        onRowClick={this.onRowClick}
                                    />
                                ) : (
                                    <FilesCoverageTable
                                        itemsProvider={new ArrayItemProvider(getTableItems(props.tableItems, !!this.hasMoreChangeData))}
                                        loadMore={this.hasMoreChangeData ? this.loadMoreData : undefined}
                                        onRowClick={this.onRowClick}
                                    />
                                )}
                                {props.fileCodeCoverageItem ? (
                                    <WrappedComponent
                                        dependencies={["ms.vss-codecoverage-ci-web.codecoverage-ci-file-coverage-pane"]}
                                        wrappedType="CC.CI.FileCoverageViewer"
                                        runViewArtifact={this.props.runViewArtifact}
                                        {...props.fileCodeCoverageItem}
                                        onMount={this.onCodeCoverageFileViewerMount}
                                        onClose={this.onFileCoverageViewClose}
                                    />
                                ) : null}
                            </>
                        );
                    }}
                </Observer>
            );
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this.props.filter.unsubscribe(this.onFilterChanged);
    }

    private onCodeCoverageFileViewerMount = (): void => {
        publish(this.context.pageContext, TelemetryEvents.CodeCoverageFileDetailsViewed, this.props.runViewArtifact.buildId, {
            source: "ChangedTab"
        });
    };

    private onRowClick = (event: React.SyntheticEvent<HTMLElement>, treeItem: IFileCoverageTableItem): void => {
        this.fileCodeCoverageItem.value = treeItem;

        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.startScenario(PerfScenarios.FileCoverageDetails, true);
    };

    private onFileCoverageViewClose = (): void => {
        this.fileCodeCoverageItem.value = undefined;
    };

    private loadMoreData = (): void => {
        if (!this.loadingMore) {
            this.loadingMore = true;
            this.changedTabViewService
                .getBatchedCoverageChangesAsync(this.currentTableItems.value.length - 1)
                .then((response: ICodeCoverageChangeResponse) => {
                    this.hasMoreChangeData = response.hasMoreData;
                    this.currentTableItems.value = this.currentTableItems.value.concat(response.fileCoverageChanges);
                    this.loadingMore = false;
                });
        }
    };

    private onFilterChanged = (value: IFilterState): void => {
        const keywordState = value["changes-tab-filter"];
        if (keywordState) {
            this.filterItems(keywordState.value);
        }
    };

    private filterItems = (searchText: string): void => {
        if (searchText) {
            this.filterInProgress.value = true;
            this.isFilterMode.value = true;
            this.changedTabViewService.getFilteredCoverageChangesAsync(searchText).then(
                (items: IFileCoverageTableItem[]) => {
                    this.filteredItems.value = items;
                    this.filterInProgress.value = false;
                },
                () => {
                    this.filterInProgress.value = false;
                }
            );
        } else {
            this.onFilterClear();
        }
    };

    private onFilterClear = (): void => {
        this.filterInProgress.value = false;
        this.isFilterMode.value = false;

        this.filteredItems.value = [];

        this.props.filter.reset();
    };
}
